export default class AutocompleteSelect {
  constructor($select, options = {}) {
    if ($select === null) {
      throw "Empty select received";
    }
    this.$select = $select;
    this.options = this.validateOptions(options);
    this.originalOptions = [];
    this.$userCreatedOption = null;
    this.firstClick = true;

    this.setupHTML();
    this.setupListeners();

    if (typeof window.autocompleteInstances === "undefined") {
      window.autocompleteInstances = {};
    }
    window.autocompleteInstances[this.getId()] = this;
  }

  getId() {
    if (this.$select.id) {
      return this.$select.id;
    }

    const $form = this.$select.closest("form");
    return ($form ? $form.getAttribute("id") : "") + "-" + this.$select.name;
  }

  validateOptions(options) {
    if (typeof options.caseSensitive === "undefined") {
      options.caseSensitive = false;
    }
    if (typeof options.allowNewValues === "undefined") {
      options.allowNewValues = false;
    }
    if (typeof options.inputClasses === "undefined") {
      options.inputClasses = [];
    }
    if (typeof options.dropdownClasses === "undefined") {
      options.dropdownClasses = [];
    }
    if (typeof options.display === "undefined") {
      options.display = "block";
    }
    if (typeof options.eventToListen === "undefined") {
      options.eventToListen = "click";
    }
    if (typeof options.limit === "undefined") {
      options.limit = 10;
    } else {
      options.limit = parseInt(options.limit);
    }
    if (typeof options.allowHTML === "undefined") {
      options.allowHTML = false;
    }

    return options;
  }

  setupHTML() {
    const html = `
        <div class="ac-select-container w-100" style="display: none">
            <div class="ac-select-dropdown w-100">
                <div class="ac-select-search-box">
                    <input type="text" placeholder="Search...">
                </div>
                <ul class="list" style="max-height:200px; overflow: scroll">
                </ul>
            </div>
        </div>`;
    const $currentSelectParent = this.$select.parentNode;
    let div = document.createElement("div");
    div.innerHTML = html;
    div.style.position = "relative";

    this.$container = div.firstElementChild;
    this.$container.dataset.for = this.$select.name;
    this.$listContainer = this.$container.querySelector(".list");
    this.$input = this.$container.querySelector("input");

    this.options.inputClasses.forEach((className) => {
      this.$input.classList.add(className);
    });
    this.options.dropdownClasses.forEach((className) => {
      this.$listContainer.classList.add(className);
    });

    let shown = 0;
    this.$select.querySelectorAll("option").forEach((el) => {
      this.originalOptions.push({
        text: el.innerText,
        value: el.value,
      });
      if (shown < this.options.limit) {
        this.insertOption(el.innerText, el.value);
      }
      shown++;
    });
    div.prepend(this.$select);
    $currentSelectParent.appendChild(div);
  }

  insertOption(text, value, isNew) {
    const li = document.createElement("li");

    if (this.options.allowHTML) {
      li.innerHTML = text;
    } else {
      li.innerText = text;
    }

    li.dataset.value = value;

    li.addEventListener(this.options.eventToListen, () => {
      if (isNew) {
        this.addNewEntry(text, value, true);
      } else {
        this.select(value);
      }
    });

    this.$listContainer.appendChild(li);
  }

  select(val) {
    this.$select.value = val;
    this.$select.dispatchEvent(new Event("change"));

    this.resetContainer();
  }

  setContainerVisible(enable) {
    this.$container.style.display = enable ? this.options.display : "none";
  }

  toggleContainerVisible() {
    this.$container.style.display =
      this.$container.style.display == "none" ? this.options.display : "none";
  }

  setupListeners() {
    this.$select.addEventListener("mousedown", (e) => {
      e.preventDefault();
      // so filter events are triggered
      if (this.firstClick) {
        this.filterOptionsBy("");
        this.firstClick = false;
      }
      this.toggleContainerVisible();
      if (this.$container.style.display == this.options.display) {
        this.$input.focus();
      }
    });

    document.addEventListener("click", (e) => {
      if (
        this.$container.style.display == this.options.display &&
        !this.$container.contains(e.target) &&
        e.target != this.$select
      ) {
        this.setContainerVisible(false);
      }
    });

    this.$input.addEventListener("input", (e) => {
      this.filterOptionsBy(this.$input.value);
    });

    if (this.options.allowNewValues) {
      this.$input.addEventListener("keyup", (e) => {
        if (e.key === "Enter" && this.$input.value.length > 0) {
          this.addNewEntry(this.$input.value, this.$input.value, true);
        }
      });
    }
  }

  add(text, val, setAsSelected) {
    this.originalOptions.push({
      text: text,
      value: val,
    });
    return this.addNewEntry(text, val, false, setAsSelected);
  }

  addNewEntry(text, val, removePreviousEntry, setAsSelected) {
    if (typeof removePreviousEntry === "undefined") {
      removePreviousEntry = false;
    }
    if (typeof setAsSelected === "undefined") {
      setAsSelected = true;
    }
    if (this.$userCreatedOption != null && removePreviousEntry) {
      this.$select.removeChild(this.$userCreatedOption);
    }
    this.$userCreatedOption = document.createElement("option");
    if (this.options.allowHTML) {
      this.$userCreatedOption.innerHTML = text;
    } else {
      this.$userCreatedOption.innerText = text;
    }

    this.$userCreatedOption.value = val;

    this.$select.appendChild(this.$userCreatedOption);
    if (setAsSelected) {
      this.$select.value = val;
    }
    this.$select.dispatchEvent(new Event("change"));

    this.resetContainer();
    return this.$userCreatedOption;
  }

  resetContainer() {
    this.$input.value = "";
    this.setContainerVisible(false);
    this.filterOptionsBy(""); // so all options are shown again if he clicks back
  }

  filterOptionsBy(query) {
    if (!this.options.caseSensitive) {
      query = query.toLowerCase();
    }

    this.$listContainer.innerHTML = "";
    let shown = 0;
    let results = [];

    this.originalOptions.forEach((option) => {
      if (shown >= this.options.limit) {
        return;
      }
      const text = !this.options.caseSensitive
        ? option.text.toLowerCase()
        : option.text;

      if (text.includes(query)) {
        results.push(option);
        shown++;
      }
    });

    if (this.options.allowNewValues && query) {
      results.push({
        text: query,
        value: query,
        isNew: true,
      });
    }

    if (typeof this.options.customFilter !== "undefined") {
      results = this.options.customFilter(results, this);
    }

    results.forEach((option) => {
      if (typeof option.isNew !== "undefined") {
        this.insertOption(option.text, option.value, true);
      } else {
        this.insertOption(option.text, option.value);
      }
    });
  }
}

window.AutocompleteSelect = AutocompleteSelect;
